import { createClient, SupabaseClient } from "@supabase/supabase-js";
import type {
	Asset,
	AssetInput,
	OwnedPortfolioEntry,
	Portfolio,
	PortfolioAccess,
	PortfolioInput,
	PortfolioListEntry,
	TransactionInput,
} from "../core";

/** Auth, realtime and transaction mutations only — portfolio reads go through src/data providers. */
class ClientSupabase {
	private supabase: SupabaseClient;

	constructor() {
		const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
		const supabaseKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

		this.supabase = createClient(supabaseUrl, supabaseKey);
	}

	subscribeToTransactionChanges(portfolioId: string, callback: () => void) {
		const channel = this.supabase
			.channel(`portfolio_changes:${portfolioId}`)
			.on(
				"postgres_changes",
				{
					event: "*",
					schema: "public",
					table: "transaction_history",
					filter: `portfolio_id=eq.${portfolioId}`,
				},
				callback,
			)
			.subscribe();

		return () => {
			this.supabase.removeChannel(channel);
		};
	}

	subscribeToAuth(callback: (session: { user: unknown | null } | null) => void) {
		return this.supabase.auth.onAuthStateChange((_, session) => {
			callback(session);
		});
	}

	async signOut() {
		const { error } = await this.supabase.auth.signOut();
		if (error) {
			console.error("Error signing out:", error);
			throw error;
		}
	}

	async signInWithGoogle(redirectUrl: string) {
		const { error } = await this.supabase.auth.signInWithOAuth({
			provider: "google",
			options: {
				redirectTo: redirectUrl,
			},
		});
		if (error) {
			console.error("Error signing in with Google:", error);
			throw error;
		}
	}

	async getSession() {
		const { data, error } = await this.supabase.auth.getSession();
		if (error) {
			console.error("Error getting session:", error);
			return { session: null };
		}
		return data;
	}

	async getUserId(): Promise<string | null> {
		const { data, error } = await this.supabase.auth.getUser();
		if (error) {
			console.error("Error getting user:", error);
			return null;
		}
		return data.user?.id ?? null;
	}

	/** Owned and shared portfolios; RLS decides which rows come back. */
	async listPortfolios(): Promise<PortfolioListEntry[]> {
		const [userId, portfoliosResult, accessResult] = await Promise.all([
			this.getUserId(),
			this.supabase.from("portfolios").select("*"),
			this.supabase.from("portfolio_access").select("*"),
		]);

		if (portfoliosResult.error) {
			console.error("Error listing portfolios:", portfoliosResult.error);
			throw portfoliosResult.error;
		}
		if (accessResult.error) {
			console.error("Error listing portfolio access:", accessResult.error);
			throw accessResult.error;
		}

		const accessByPortfolio = new Map<string, PortfolioAccess>();
		for (const access of (accessResult.data ?? []) as PortfolioAccess[]) {
			accessByPortfolio.set(access.portfolio_id, access);
		}

		return ((portfoliosResult.data ?? []) as Portfolio[])
			.map((portfolio) => {
				const isOwner = portfolio.owner_id === userId;
				return {
					portfolio,
					isOwner,
					forcePrivacyMode: isOwner ? false : (accessByPortfolio.get(portfolio.id)?.force_privacy_mode ?? false),
				};
			})
			.sort((a, b) => Number(b.isOwner) - Number(a.isOwner) || a.portfolio.name.localeCompare(b.portfolio.name));
	}

	async addTransaction(portfolioId: string, transaction: TransactionInput) {
		const { error } = await this.supabase.from("transaction_history").insert({ ...transaction, portfolio_id: portfolioId });
		if (error) {
			console.error("Error adding transaction:", error);
			throw error;
		}
	}

	async updateTransaction(id: string, portfolioId: string, transaction: TransactionInput) {
		const { error } = await this.supabase
			.from("transaction_history")
			.update({ ...transaction, portfolio_id: portfolioId })
			.eq("id", id);
		if (error) {
			console.error("Error updating transaction:", error);
			throw error;
		}
	}

	async removeTransaction(id: string) {
		const { error } = await this.supabase.from("transaction_history").delete().eq("id", id);
		if (error) {
			console.error("Error removing transaction:", error);
			throw error;
		}
	}

	/** Portfolios owned by the signed-in user, each with its full list of access grants. */
	async listOwnedPortfoliosWithAccess(): Promise<OwnedPortfolioEntry[]> {
		const userId = await this.getUserId();

		const [portfoliosResult, accessResult] = await Promise.all([
			this.supabase
				.from("portfolios")
				.select("*")
				.eq("owner_id", userId ?? ""),
			this.supabase.rpc("list_owned_portfolio_access"),
		]);

		if (portfoliosResult.error) {
			console.error("Error listing owned portfolios:", portfoliosResult.error);
			throw portfoliosResult.error;
		}
		if (accessResult.error) {
			console.error("Error listing portfolio access:", accessResult.error);
			throw accessResult.error;
		}

		const accessByPortfolio = new Map<string, PortfolioAccess[]>();
		for (const access of (accessResult.data ?? []) as PortfolioAccess[]) {
			const list = accessByPortfolio.get(access.portfolio_id) ?? [];
			list.push(access);
			accessByPortfolio.set(access.portfolio_id, list);
		}

		return ((portfoliosResult.data ?? []) as Portfolio[])
			.map((portfolio) => ({ portfolio, access: accessByPortfolio.get(portfolio.id) ?? [] }))
			.sort((a, b) => a.portfolio.name.localeCompare(b.portfolio.name));
	}

	async createPortfolio(input: PortfolioInput): Promise<Portfolio> {
		const { data, error } = await this.supabase.from("portfolios").insert(input).select().single();
		if (error) {
			console.error("Error creating portfolio:", error);
			throw error;
		}
		return data as Portfolio;
	}

	async listAssets(): Promise<Asset[]> {
		const { data, error } = await this.supabase.from("assets").select("*").order("ticker", { ascending: true });
		if (error) {
			console.error("Error listing assets:", error);
			throw error;
		}
		return (data ?? []) as Asset[];
	}

	async upsertAsset(input: AssetInput): Promise<Asset> {
		const { data, error } = await this.supabase.from("assets").upsert(input, { onConflict: "ticker" }).select().single();
		if (error) {
			console.error("Error upserting asset:", error);
			throw error;
		}
		return data as Asset;
	}

	/** Blocked server-side (delete_portfolio RPC) if the portfolio still has transactions. */
	async deletePortfolio(portfolioId: string) {
		const { error } = await this.supabase.rpc("delete_portfolio", { p_portfolio_id: portfolioId });
		if (error) {
			console.error("Error deleting portfolio:", error);
			throw error;
		}
	}

	async grantAccess(portfolioId: string, viewerId: string, forcePrivacyMode: boolean) {
		const { error } = await this.supabase.rpc("grant_portfolio_access", {
			p_portfolio_id: portfolioId,
			p_viewer_id: viewerId,
			p_force_privacy_mode: forcePrivacyMode,
		});
		if (error) {
			console.error("Error granting access:", error);
			throw error;
		}
	}

	async revokeAccess(accessId: string) {
		const { error } = await this.supabase.rpc("revoke_portfolio_access", { p_access_id: accessId });
		if (error) {
			console.error("Error revoking access:", error);
			throw error;
		}
	}

	async updateAccessPrivacy(accessId: string, forcePrivacyMode: boolean) {
		const { error } = await this.supabase.rpc("update_portfolio_access_privacy", {
			p_access_id: accessId,
			p_force_privacy_mode: forcePrivacyMode,
		});
		if (error) {
			console.error("Error updating access privacy mode:", error);
			throw error;
		}
	}

	getClient(): SupabaseClient {
		return this.supabase;
	}
}

const client = new ClientSupabase();

export { client };
